import React, { useRef, useState, forwardRef, useImperativeHandle, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { AppDispatch, IRootState } from '../../../../redux/rootReducer';
import Loader from '../../../common/loader/Loader';
import CustModal from '../../../common/custmodal/CustModal';
import MageDotsIcon from '../../../svgIcons/MageDotsIcon';
import AddVendorsAppointment from '../../addVendors/AddVendorsAppointment';
import ViewVendorAppointment from './ViewVendorAppointment';
import { deleteVendorAppointment, getVendorAppointment } from '../../../../redux/vendor/actions/vendor.action';
import { hasPermission } from '../../../auth/login/Login';
import { parsePhoneNumber } from "awesome-phonenumber";
import styles from '../VendorAppointments.module.css'

interface VendorMapped {
    locationId: string;
    vendorName: string;
    vendorEmail: string;
    startDate: string;
    endDate: string;
    startTime: string;
    endTime: string;
    propertyName: string;
    vendorPhone: string;
    locationName: string;
    locker: string;
    id: string;
    hasLockerAccess: number;
}

interface VendorAppointmentsTableProps {
    searchTerm: string;
    pageNumber: number;
    pageLimit: number;
    totalCount: number;
    setTotalCount: React.Dispatch<React.SetStateAction<number>>;
    onPageChange: (page: number) => void;
    setShowToast: React.Dispatch<React.SetStateAction<boolean>>;
    setToastMessage: React.Dispatch<React.SetStateAction<string>>;
    setToastType: React.Dispatch<React.SetStateAction<string>>;
}

const VendorAppointmentsTable = forwardRef<{ refetchData: () => void }, VendorAppointmentsTableProps>(({
    searchTerm,
    pageNumber,
    pageLimit,
    totalCount,
    setTotalCount,
    onPageChange,
    setShowToast,
    setToastMessage,
    setToastType
}, ref) => {
    const dispatch: AppDispatch = useDispatch();
    const [showActionBox, setShowActionBox] = useState<string | null>(null);
    const [actionBoxPosition, setActionBoxPosition] = useState<'top' | 'bottom'>('bottom');
    const actionButtonRefs = useRef<Record<string, HTMLTableRowElement | null>>({});
    const [viewVendor, setViewVendor] = useState(false);
    const [selectedVendor, setSelectedVendor] = useState<typeof formattedVendorList[0] | null>(null);
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const [isAPILoading, setIsAPILoading] = useState(false);

    const canDeleteVendor = hasPermission('Self-Guided Tours', 'Vendor', 'delete');
    const canUpdateVendor = hasPermission('Self-Guided Tours', 'Vendor', 'update');

    const { data: vendorAppointmentList, loading: loadingVendorAppointmentList } = useSelector((state: IRootState) => state?.vendorAppintmentListReducer);
    const getVendorAppointmentListData = () => {
        dispatch(getVendorAppointment({ search: searchTerm, page: pageNumber, limit: pageLimit, setTotalCount }));
    }

    // Expose refetch method to parent component
    useImperativeHandle(ref, () => ({
        refetchData: getVendorAppointmentListData
    }));

    // Auto-refetch when search term or page number changes
    useEffect(() => {
        getVendorAppointmentListData();
    }, [searchTerm, pageNumber]);

    const formatDateTime = (date: string, time: string) => {
        if (!date || !time) return '-';
        return `${moment(date, 'DD-MM-YYYY').format('MM-DD-YYYY')}\u00A0\u00A0${moment(time, 'HH:mm A').format('hh:mm A')}`;
    };

    const formatPhoneNumber = (phone: string) => {
        if (!phone || phone === "-") return "-";

        try {
            const phoneNumber = parsePhoneNumber(`+${phone}`);
            if (phoneNumber && phoneNumber.valid) {
                return phoneNumber.number.international;
            }
            return phone; // Return original if parsing fails
        } catch (error) {
            console.warn('Phone number parsing failed:', error);
            return phone; // Return original if parsing fails
        }
    };

    const formattedVendorList = vendorAppointmentList
        .map((vendor: VendorMapped) => ({
            locationId: vendor.locationId,
            vendorName: vendor.vendorName,
            vendorEmail: vendor.vendorEmail ? vendor.vendorEmail : "-",
            startDate: formatDateTime(vendor.startDate, vendor.startTime),
            endDate: formatDateTime(vendor.endDate, vendor.endTime),
            propertyName: vendor.locationName,
            vendorPhone: vendor.vendorPhone ? formatPhoneNumber(vendor.vendorPhone) : "-",
            locker: vendor?.locker ? vendor?.locker : '-',
            id: vendor.id
        }));

    const handleActionButtonClick = (index: string) => {
        const rowRef = actionButtonRefs.current[index];

        if (rowRef) {
            const rect = rowRef.getBoundingClientRect();
            const parentRect = rowRef.closest(`.${CSS.escape(styles.vendorAppointmentsTableListing)}`)?.getBoundingClientRect();

            if (parentRect) {
                const parentBottom = parentRect.bottom;

                if (rect.bottom > parentBottom - 100) {
                    setActionBoxPosition('top');
                } else {
                    setActionBoxPosition('bottom');
                }

                setShowActionBox(showActionBox !== index ? index : '');
            }
        }
    };

    const onDeleteVendorSuccess = (message: string) => {
        setIsAPILoading(false);
        setToastType('success');
        setShowToast(true);
        setToastMessage(message);
        getVendorAppointmentListData();
    }

    const onDeleteVendorFailure = (message: string) => {
        setIsAPILoading(false);
        setToastType('error');
        setShowToast(true);
        setToastMessage(message)
    }

    const onDeleteVendor = () => {
        if (selectedVendor?.id) {
            setIsAPILoading(true);
            setShowConfirmModal(false);
            dispatch(deleteVendorAppointment(selectedVendor.id, onDeleteVendorSuccess, onDeleteVendorFailure))
        }
    }

    const ActionModalBody = () => {
        return (
            <div className={styles.vendorAppointmentsActionModal}>
                <h2 className={styles.vendorAppointmentsTitle}>{`Are you sure you want to delete?`}</h2>
                <p className={styles.vendorAppointmentsDescription}>
                    Vendor appointment cannot be recovered once deleted.
                </p>
                <div className={styles.vendorAppointmentsActions}>
                    <button className={styles.vendorAppointmentsCancelButton} onClick={() => setShowConfirmModal(false)}>
                        Cancel
                    </button>
                    <button className={styles.vendorAppointmentsConfirmButton}
                        onClick={onDeleteVendor}>
                        Delete
                    </button>
                </div>
            </div >
        )
    }

    return (
        <>
            {(loadingVendorAppointmentList || isAPILoading) && <Loader />}

            <CustModal
                open={viewVendor}
                setOpen={setViewVendor}
                bodyData={
                    selectedVendor && (
                        <ViewVendorAppointment
                            setOpen={setViewVendor}
                            setShowToast={setShowToast}
                            setToastMessage={setToastMessage}
                            setToastType={setToastType}
                            vendorAppointmentList={selectedVendor}
                            onDeleteSuccess={getVendorAppointmentListData}
                        />
                    )
                }
            />

            {showConfirmModal && <CustModal
                open={showConfirmModal}
                bodyData={<ActionModalBody />}
                onCloseModal={() => {
                    setShowConfirmModal(false);
                }}
            />}

            <div className={styles.vendorAppointmentsTableListing}>
                <table>
                    <thead>
                        <tr>
                            <th>Vendor Name</th>
                            <th>Property Name</th>
                            <th>Email</th>
                            <th>Contact No</th>
                            <th>Start Date</th>
                            <th>End Date</th>
                            <th>Locker No</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {formattedVendorList?.length > 0
                            ? formattedVendorList.map((item: VendorMapped, index: number) => (
                                <tr key={index} ref={(el) => (actionButtonRefs.current[index.toString()] = el)}>
                                    <td>{item.vendorName}</td>
                                    <td>{item.propertyName}</td>
                                    <td>{item.vendorEmail}</td>
                                    <td>{item.vendorPhone}</td>
                                    <td>{item.startDate}</td>
                                    <td>{item.endDate}</td>
                                    <td>{item.locker}</td>
                                    <td>
                                        {!!showActionBox && (
                                            <div
                                                className={styles.vendorAppointmentsDropDownBackDrop}
                                                onClick={() => setShowActionBox('')}></div>
                                        )}
                                        <div className={styles.vendorAppointmentsActionButtonWrapper}>
                                            <button
                                                disabled={!(canUpdateVendor || canDeleteVendor)}
                                                className={styles.vendorAppointmentsMageDotsIcon}
                                                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                                    event.stopPropagation();
                                                    handleActionButtonClick(index?.toString());
                                                }}>
                                                <MageDotsIcon />
                                            </button>
                                            <div
                                                className={`${styles.vendorAppointmentsActionContainer} ${index?.toString() === showActionBox ? styles.active : ''
                                                    } ${actionBoxPosition === 'top' ? styles.top : ''}`}>
                                                {canUpdateVendor && <div
                                                    className={styles.vendorAppointmentsActionBox}
                                                    role="button"
                                                    onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                                        event.stopPropagation();
                                                        setShowActionBox(null);
                                                        setSelectedVendor(vendorAppointmentList[index]);
                                                        setViewVendor(true);
                                                    }}>
                                                    <span>{'View'}</span>
                                                </div>}
                                                {canDeleteVendor && <div
                                                    className={styles.vendorAppointmentsActionBox}
                                                    role="button"
                                                    onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                                        event.stopPropagation();
                                                        setShowActionBox(null);
                                                        setSelectedVendor(vendorAppointmentList[index]);
                                                        setShowConfirmModal(true);
                                                    }}>
                                                    <span>{'Delete'}</span>
                                                </div>}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ))
                            : !loadingVendorAppointmentList &&
                            (
                                <tr>
                                    <td className={styles.noData} colSpan={8}>
                                        No Vendor Appointment Found
                                    </td>
                                </tr>
                            )}
                    </tbody>
                </table>
            </div>
        </>
    );
});

VendorAppointmentsTable.displayName = 'VendorAppointmentsTable';

export default VendorAppointmentsTable;
